import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Phone, Users, Clock, AlertTriangle, Settings, Plus, Bot, Activity, TrendingUp, PhoneCall, PhoneMissed, Timer, UserCheck, Lock, Layers } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CallCenterQueues() {
  const [queues, setQueues] = useState([]);
  const [aiAgents, setAiAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('queues');
  const [newQueue, setNewQueue] = useState({
    name: '',
    description: '',
    queue_type: 'inbound',
    routing_strategy: 'round_robin',
    max_wait_seconds: 120,
    ai_enabled: true,
    ai_answers_first: false,
    sla_answer_seconds: 30
  });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [queuesRes, agentsRes] = await Promise.all([
        axios.get(`${API}/call-center/queues`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/call-center/ai-agents`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setQueues(queuesRes.data.queues || []);
      setAiAgents(agentsRes.data.agents || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load queues');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const createQueue = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/call-center/queues`, newQueue, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Queue created successfully');
      setShowCreateDialog(false);
      setNewQueue({
        name: '',
        description: '',
        queue_type: 'inbound',
        routing_strategy: 'round_robin',
        max_wait_seconds: 120,
        ai_enabled: true,
        ai_answers_first: false,
        sla_answer_seconds: 30
      });
      fetchData();
    } catch (err) {
      console.error('Failed to create queue:', err);
      toast.error('Failed to create queue');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-500';
      case 'on_call': return 'bg-blue-500';
      case 'acw': return 'bg-yellow-500';
      case 'break': return 'bg-orange-500';
      case 'offline': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getAgentTypeLabel = (type) => {
    const labels = {
      ai_receptionist: 'Receptionist',
      ai_service_support: 'Support',
      ai_sales_caller: 'Sales Caller',
      ai_sales_qualifier: 'Qualifier',
      ai_overflow: 'Overflow'
    };
    return labels[type] || type;
  };

  // Calculate live stats
  const liveStats = {
    totalCalls: queues.reduce((sum, q) => sum + (q.calls_waiting || 0), 0),
    agentsAvailable: aiAgents.filter(a => a.status === 'available').length,
    longestWait: Math.max(...queues.map(q => q.longest_wait_seconds || 0), 0),
    aiOnline: aiAgents.filter(a => a.status !== 'offline').length
  };

  return (
    <Layout>
    <div className="p-6 space-y-6">
      {/* Phase 2 Preview Banner */}
      <Alert className="bg-indigo-50 border-indigo-200 dark:bg-indigo-900/20 dark:border-indigo-800">
        <Layers className="h-5 w-5 text-indigo-600" />
        <AlertDescription className="ml-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-semibold text-indigo-800 dark:text-indigo-300">Phase 2 Prep Mode</span>
              <span className="text-indigo-700 dark:text-indigo-400 ml-2">
                Configure queues and routing rules now. Live call distribution will activate in Active Mode.
              </span>
            </div>
            <Badge variant="outline" className="border-indigo-500 text-indigo-700 dark:text-indigo-300">
              <Lock className="h-3 w-3 mr-1" />
              Routing Inactive
            </Badge>
          </div>
        </AlertDescription>
      </Alert>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Queues & Live Console</h1>
          <p className="text-muted-foreground">Manage call queues and monitor agent activity in real-time</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Queue
        </Button>
      </div>

      {/* Live Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Calls in Queue</p>
                <p className="text-3xl font-bold">{liveStats.totalCalls}</p>
              </div>
              <PhoneCall className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Agents Available</p>
                <p className="text-3xl font-bold">{liveStats.agentsAvailable}</p>
              </div>
              <UserCheck className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Longest Wait</p>
                <p className="text-3xl font-bold">{liveStats.longestWait}s</p>
              </div>
              <Timer className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">AI Agents Online</p>
                <p className="text-3xl font-bold">{liveStats.aiOnline}</p>
              </div>
              <Bot className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="queues">
            <Phone className="h-4 w-4 mr-2" />
            Queues ({queues.length})
          </TabsTrigger>
          <TabsTrigger value="agents">
            <Bot className="h-4 w-4 mr-2" />
            AI Agents ({aiAgents.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="queues" className="mt-4">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : queues.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Phone className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No queues configured</p>
                <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                  Create Your First Queue
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {queues.map((queue) => (
                <Card key={queue.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{queue.name}</CardTitle>
                      <Badge variant={queue.is_active ? 'default' : 'secondary'}>
                        {queue.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <CardDescription>{queue.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-muted/50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                          <Phone className="h-4 w-4" />
                          Waiting
                        </div>
                        <p className="text-2xl font-bold">{queue.calls_waiting || 0}</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                          <Clock className="h-4 w-4" />
                          Max Wait
                        </div>
                        <p className="text-2xl font-bold">{queue.longest_wait_seconds || 0}s</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Type</span>
                        <span className="capitalize">{queue.queue_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Routing</span>
                        <span className="capitalize">{queue.routing_strategy?.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">AI Enabled</span>
                        <Badge variant={queue.ai_enabled ? 'default' : 'secondary'} className="text-xs">
                          {queue.ai_enabled ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">AI First</span>
                        <Badge variant={queue.ai_answers_first ? 'default' : 'outline'} className="text-xs">
                          {queue.ai_answers_first ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">SLA Target</span>
                        <span>{queue.sla_answer_seconds}s</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Settings className="h-4 w-4 mr-1" />
                        Configure
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1">
                        <Users className="h-4 w-4 mr-1" />
                        Members
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="agents" className="mt-4">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : aiAgents.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Bot className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No AI agents configured</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {aiAgents.map((agent) => (
                <Card key={agent.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <Bot className="h-6 w-6 text-purple-600" />
                        </div>
                        <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-background ${getStatusColor(agent.status)}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{agent.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {getAgentTypeLabel(agent.agent_type)}
                        </p>
                        <Badge variant="outline" className="mt-1 capitalize">
                          {agent.status?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-muted/50 rounded p-2 text-center">
                        <p className="text-lg font-bold">{agent.total_interactions || 0}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                      </div>
                      <div className="bg-muted/50 rounded p-2 text-center">
                        <p className="text-lg font-bold text-green-600">
                          {agent.total_interactions ? Math.round((agent.successful_interactions / agent.total_interactions) * 100) : 0}%
                        </p>
                        <p className="text-xs text-muted-foreground">Success</p>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Avg Handle</span>
                        <span>{agent.avg_handle_time_seconds || 0}s</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Escalated</span>
                        <span>{agent.escalated_interactions || 0}</span>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1">
                      {agent.can_answer_calls && <Badge variant="outline" className="text-xs">Calls</Badge>}
                      {agent.can_send_sms && <Badge variant="outline" className="text-xs">SMS</Badge>}
                      {agent.can_schedule_appointments && <Badge variant="outline" className="text-xs">Schedule</Badge>}
                      {agent.can_create_leads && <Badge variant="outline" className="text-xs">Leads</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Queue Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Queue</DialogTitle>
            <DialogDescription>Configure a new call queue for your team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Queue Name</Label>
              <Input
                value={newQueue.name}
                onChange={(e) => setNewQueue({ ...newQueue, name: e.target.value })}
                placeholder="e.g., Inbound Sales"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={newQueue.description}
                onChange={(e) => setNewQueue({ ...newQueue, description: e.target.value })}
                placeholder="Queue description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Queue Type</Label>
                <Select
                  value={newQueue.queue_type}
                  onValueChange={(v) => setNewQueue({ ...newQueue, queue_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inbound">Inbound</SelectItem>
                    <SelectItem value="outbound">Outbound</SelectItem>
                    <SelectItem value="blended">Blended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Routing Strategy</Label>
                <Select
                  value={newQueue.routing_strategy}
                  onValueChange={(v) => setNewQueue({ ...newQueue, routing_strategy: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="round_robin">Round Robin</SelectItem>
                    <SelectItem value="longest_idle">Longest Idle</SelectItem>
                    <SelectItem value="skills_based">Skills Based</SelectItem>
                    <SelectItem value="ai_first">AI First</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max Wait (seconds)</Label>
                <Input
                  type="number"
                  value={newQueue.max_wait_seconds}
                  onChange={(e) => setNewQueue({ ...newQueue, max_wait_seconds: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>SLA Target (seconds)</Label>
                <Input
                  type="number"
                  value={newQueue.sla_answer_seconds}
                  onChange={(e) => setNewQueue({ ...newQueue, sla_answer_seconds: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Enable AI Agents</Label>
              <Switch
                checked={newQueue.ai_enabled}
                onCheckedChange={(v) => setNewQueue({ ...newQueue, ai_enabled: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>AI Answers First</Label>
              <Switch
                checked={newQueue.ai_answers_first}
                onCheckedChange={(v) => setNewQueue({ ...newQueue, ai_answers_first: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createQueue} disabled={!newQueue.name}>Create Queue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
